import React from 'react';
import './Legal.css';

const Terms: React.FC = () => {
  return (
    <div className="legal-page">
      <div className="container">
        <h1>Terms of Service</h1>
        <div className="legal-content">
          <p>
            These Terms of Service ("Terms") govern your use of all applications, websites, services, 
            and digital products developed and operated by FreioLabs ("we," "us," "our," or "Service Provider"). 
            This includes but is not limited to mobile applications, web applications, software, 
            APIs, and any related services (collectively referred to as "Services").
          </p>

          <h2>1. Agreement to Terms</h2>
          <p>
            By accessing, downloading, installing, or using any of our Services, you agree to be bound by these Terms. 
            If you do not agree to these Terms, please do not use our Services. We strongly recommend that you read 
            and understand these Terms thoroughly before using any of our Services.
          </p>

          <h2>2. Scope of Services</h2>
          <p>
            FreioLabs develops and operates various digital products including:
          </p>
          <ul>
            <li>Mobile applications for iOS and Android platforms</li>
            <li>Web applications and websites</li>
            <li>Software tools and utilities</li>
            <li>Application Programming Interfaces (APIs)</li>
            <li>Digital services and platforms</li>
            <li>Any future products or services we may develop</li>
          </ul>

          <h2>3. Intellectual Property Rights</h2>
          <p>
            All Services and their original content, features, and functionality are and will remain the exclusive 
            property of FreioLabs and its licensors. The Services are protected by copyright, trademark, and other laws. 
            Our trademarks and trade dress may not be used in connection with any product or service without our prior 
            written consent. You may not:
          </p>
          <ul>
            <li>Copy, modify, distribute, sell, or lease any part of our Services</li>
            <li>Reverse engineer or attempt to extract the source code of our Services</li>
            <li>Create derivative works based on our Services</li>
            <li>Use our Services for any commercial purpose without authorization</li>
          </ul>

          <h2>4. User Accounts and Responsibilities</h2>
          <p>
            When you create an account with us, you must provide information that is accurate, complete, and current 
            at all times. You are responsible for safeguarding your account credentials and for all activities that 
            occur under your account. You must immediately notify us of any unauthorized uses of your account or any 
            other breaches of security.
          </p>

          <h2>5. Acceptable Use</h2>
          <p>
            You agree not to use our Services:
          </p>
          <ul>
            <li>For any unlawful purpose or to solicit others to take unlawful actions</li>
            <li>To violate any international, federal, provincial, or state regulations, rules, laws, or local ordinances</li>
            <li>To infringe upon or violate our intellectual property rights or the intellectual property rights of others</li>
            <li>To harass, abuse, insult, harm, defame, slander, disparage, intimidate, or discriminate</li>
            <li>To submit false or misleading information</li>
            <li>To upload or transmit viruses or any other type of malicious code</li>
            <li>To spam, phish, pharm, pretext, spider, crawl, or scrape</li>
          </ul>

          <h2>6. Privacy and Data Protection</h2>
          <p>
            Your privacy is important to us. Our collection and use of personal information is governed by our Privacy Policy, 
            which is incorporated into these Terms by reference. By using our Services, you consent to the collection and 
            use of your information as outlined in our Privacy Policy.
          </p>

          <h2>7. Third-Party Services</h2>
          <p>
            Our Services may integrate with or contain links to third-party websites, applications, or services that are 
            not owned or controlled by FreioLabs. These may include:
          </p>
          <ul>
            <li>Google Play Services</li>
            <li>Apple App Store services</li>
            <li>Expo platform services</li>
            <li>Analytics and crash reporting services</li>
            <li>Payment processing services</li>
            <li>Social media platforms</li>
            <li>Other third-party APIs and services</li>
          </ul>
          <p>
            We have no control over and assume no responsibility for the content, privacy policies, or practices of any 
            third-party services. You acknowledge and agree that FreioLabs shall not be responsible or liable for any 
            damage or loss caused by your use of any third-party services.
          </p>

          <h2>8. Fees and Payments</h2>
          <p>
            Some of our Services may be provided free of charge, while others may require payment. We reserve the right to:
          </p>
          <ul>
            <li>Charge fees for any of our Services</li>
            <li>Modify our pricing structure at any time</li>
            <li>Offer subscription-based or one-time payment models</li>
            <li>Provide free trial periods or promotional pricing</li>
          </ul>
          <p>
            Any charges will be clearly communicated to you before you are required to pay. All payments are non-refundable 
            unless otherwise stated or required by law.
          </p>

          <h2>9. Service Availability and Updates</h2>
          <p>
            We strive to maintain continuous availability of our Services, but we cannot guarantee uninterrupted access. 
            Our Services may be temporarily unavailable due to maintenance, updates, or circumstances beyond our control. 
            We reserve the right to:
          </p>
          <ul>
            <li>Modify, suspend, or discontinue any Service at any time</li>
            <li>Update our Services to improve functionality, security, or performance</li>
            <li>Change system requirements or compatibility</li>
            <li>Migrate to new platforms or technologies</li>
          </ul>

          <h2>10. Device and Network Requirements</h2>
          <p>
            Use of our Services may require:
          </p>
          <ul>
            <li>Compatible devices and operating systems</li>
            <li>Internet connectivity (Wi-Fi or mobile data)</li>
            <li>Sufficient device storage and memory</li>
            <li>Updated software and security patches</li>
          </ul>
          <p>
            You are responsible for any data charges incurred while using our Services. We strongly recommend against 
            modifying your device's operating system (jailbreaking, rooting) as this may compromise security and 
            functionality of our Services.
          </p>

          <h2>11. Limitation of Liability</h2>
          <p>
            To the fullest extent permitted by applicable law, FreioLabs and its affiliates, officers, employees, 
            agents, partners, and licensors shall not be liable for any indirect, incidental, special, consequential, 
            or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible 
            losses, resulting from your use of our Services.
          </p>

          <h2>12. Disclaimer of Warranties</h2>
          <p>
            Our Services are provided on an "AS IS" and "AS AVAILABLE" basis. We make no representations or warranties 
            of any kind, express or implied, as to the operation of our Services or the information, content, materials, 
            or products included therein. You expressly agree that your use of our Services is at your sole risk.
          </p>

          <h2>13. Indemnification</h2>
          <p>
            You agree to defend, indemnify, and hold harmless FreioLabs and its affiliates from and against any and all 
            claims, damages, obligations, losses, liabilities, costs, or debt, and expenses (including but not limited 
            to attorney's fees) arising from your use of our Services or your violation of these Terms.
          </p>

          <h2>14. Termination</h2>
          <p>
            We may terminate or suspend your access to our Services immediately, without prior notice or liability, 
            for any reason whatsoever, including without limitation if you breach the Terms. Upon termination, your 
            right to use our Services will cease immediately, and you may be required to delete our applications 
            from your devices.
          </p>

          <h2>15. Governing Law</h2>
          <p>
            These Terms shall be interpreted and governed by the laws of the jurisdiction in which FreioLabs operates, 
            without regard to its conflict of law provisions. Any disputes arising from these Terms or your use of our 
            Services will be subject to the exclusive jurisdiction of the courts in that jurisdiction.
          </p>

          <h2>16. Changes to Terms</h2>
          <p>
            We reserve the right to modify these Terms at any time. If we make material changes to these Terms, we will 
            notify you by posting the new Terms on this page and updating the "Last Updated" date. Your continued use 
            of our Services after such modifications constitutes your acceptance of the updated Terms.
          </p>

          <h2>17. Severability</h2>
          <p>
            If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions 
            will remain in effect. This does not affect the validity and enforceability of any other provision.
          </p>

          <h2>18. Contact Information</h2>
          <p>
            If you have any questions about these Terms of Service, please contact us at:
          </p>
          <p>
            <strong>Email:</strong> legal@freiolabs.com<br/>
            <strong>Website:</strong> freiolabs.com
          </p>

          <p className="effective-date">
            Last Updated: {new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>

          <div className="related-documents">
            <h2>Related Documents</h2>
            <ul>
              <li><a href="/privacy">Privacy Policy</a></li>
              <li><a href="/liability">Release of Liability</a></li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;